import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  SiAngular,
  SiDotnet,
  SiGo,
  SiHtml5,
  SiNextdotjs,
  SiNodedotjs,
  SiPython,
  SiReact,
  SiVite,
  SiVuedotjs,
} from 'react-icons/si';
import { ordersService, pricingService, servicesService } from '../../services';
import { PricingAddon, PricingPlan } from '../../services/pricing.service';
import { ProductCategory } from '../../constants/productCategory';
import { useAuth } from '../../contexts/AuthContext';
import { useProducts } from '../../contexts/ProductsContext';
import { useTranslation } from 'react-i18next';
import { ProjectConfig, DeploymentType } from '../../types/deployApp.type';
import ProgressSteps from './components/ProgressSteps';
import Step1DeployApp from './steps/Step1DeployApp';
import Step2DeployApp from './steps/Step2DeployApp';
import Step3DeployApp from './steps/Step3DeployApp';
import { getPlanById, getPlansByFilters, planConfigToComponentFormat, DeploymentType as ConfigDeploymentType } from '../../config/deployAppPlans.config';

const TYPE_FAMILY = 'APP_DEPLOY';
const SERVER_OS_TYPE = 'GENERIC';
const SERVER_OS_FAMILY = 'SERVER';
const STATIC_OS_FAMILY = 'STATIC';

const formatCurrency = (amount?: number, currency?: string): string => {
  if (amount === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits: 2,
  }).format(amount);
};

type PlanOption = { id: string; name: string; price: number | null; features: string[] };

const CreateDeployAppServerPricing: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { products: allProducts, getProductsByCategory } = useProducts();
  const [step, setStep] = useState(1);
  const [isCreating, setIsCreating] = useState(false);
  const [config, setConfig] = useState<ProjectConfig>({
    name: '',
    description: '',
    type: '',
    repository: '',
    branch: 'main',
    framework: '',
    environment: '',
    region: 'us-east-1',
    autoDeploy: true,
    premiumLogs: false,
    environmentVariables: [],
    deploymentType: 'prod_lite',
    subdomain: '',
    slug: '',
    runtime: undefined,
    startMode: undefined,
    targetMode: undefined,
  });

  // Legacy static selection.
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  // New server pricing selection.
  const [serverPlans, setServerPlans] = useState<PricingPlan[]>([]);
  const [selectedServerPlanCode, setSelectedServerPlanCode] = useState<string | null>(null);
  const [selectedServerVariantCode, setSelectedServerVariantCode] = useState<string | null>(null);
  const [serverAddons, setServerAddons] = useState<PricingAddon[]>([]);
  const [selectedServerAddonCodes, setSelectedServerAddonCodes] = useState<string[]>([]);
  const [isLoadingServerPlans, setIsLoadingServerPlans] = useState(false);
  const [isLoadingServerAddons, setIsLoadingServerAddons] = useState(false);
  const [selectedLogsPlan, setSelectedLogsPlan] = useState<string | null>(null);
  const [networkMode, setNetworkMode] = useState<'shared' | 'tenant' | null>(null);
  const [computeMode, setComputeMode] = useState<'shared' | 'dedicated' | null>(null);

  const products = allProducts[ProductCategory.APP_DEPLOY] || [];

  const staticProjectTypes = [
    { id: 'html', name: 'HTML + JavaScript', icon: <SiHtml5 className="text-3xl" /> },
    { id: 'react', name: 'React', icon: <SiReact className="text-3xl" style={{ color: '#61DAFB' }} /> },
    { id: 'vue', name: 'Vue.js', icon: <SiVuedotjs className="text-3xl" style={{ color: '#4FC08D' }} /> },
    { id: 'angular', name: 'Angular', icon: <SiAngular className="text-3xl" style={{ color: '#DD0031' }} /> },
    { id: 'vite', name: 'Vite', icon: <SiVite className="text-3xl" style={{ color: '#646CFF' }} /> },
  ];
  const backendProjectTypes = [
    { id: 'nodejs', name: 'Node.js', icon: <SiNodedotjs className="text-3xl" style={{ color: '#339933' }} /> },
    { id: 'python', name: 'Python', icon: <SiPython className="text-3xl" style={{ color: '#3776AB' }} /> },
    { id: 'go', name: 'Go', icon: <SiGo className="text-3xl" style={{ color: '#00ADD8' }} /> },
    { id: 'netcore', name: '.NET Core', icon: <SiDotnet className="text-3xl" style={{ color: '#512BD4' }} /> },
    { id: 'nextjs', name: 'Next.js', icon: <SiNextdotjs className="text-3xl" style={{ color: '#000000' }} /> },
    { id: 'nuxt', name: 'Nuxt.js', icon: <SiVuedotjs className="text-3xl" style={{ color: '#00DC82' }} /> },
  ];
  const projectTypes = [...staticProjectTypes, ...backendProjectTypes];
  const projectTypeDescriptions: Record<string, string> = {
    html: 'Static HTML, CSS and JavaScript.',
    nodejs: 'Node.js server application.',
    python: 'Python server application.',
    go: 'Go server application.',
    netcore: '.NET server application.',
    angular: 'Angular frontend application.',
    react: 'React frontend application.',
    vite: 'Vite frontend application.',
    vue: 'Vue frontend application.',
    nextjs: 'Next.js application.',
    nuxt: 'Nuxt application.',
  };

  const isStaticTechnology = (type: string) => staticProjectTypes.some((t) => t.id === type);
  const isSSRTechnology = (type: string, framework: string) =>
    (type === 'nextjs' && framework === 'nextjs-ssr') || (type === 'nuxt' && framework === 'nuxt-ssr');
  const isSSGTechnology = (type: string, framework: string) =>
    (type === 'nextjs' && framework === 'nextjs-ssg') || (type === 'nuxt' && framework === 'nuxt-ssg');
  const isStaticProject = isStaticTechnology(config.type) || isSSGTechnology(config.type, config.framework);
  const isServerProject =
    (isSSRTechnology(config.type, config.framework) || backendProjectTypes.some((t) => t.id === config.type)) &&
    !isSSGTechnology(config.type, config.framework);
  const isPricingProject = isServerProject || isStaticProject;
  const pricingOsFamily = isServerProject ? SERVER_OS_FAMILY : isStaticProject ? STATIC_OS_FAMILY : null;

  useEffect(() => {
    if (accessToken && products.length === 0) getProductsByCategory(ProductCategory.APP_DEPLOY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    setSelectedPlan(null);
    setSelectedServerPlanCode(null);
    setSelectedServerVariantCode(null);
    setServerAddons([]);
    setSelectedServerAddonCodes([]);
  }, [config.type, config.framework, config.deploymentType]);

  const groupedServerPlans = useMemo(() => {
    const grouped = new Map<string, PricingPlan[]>();
    serverPlans.forEach((p) => {
      if (!grouped.has(p.planCode)) grouped.set(p.planCode, []);
      grouped.get(p.planCode)!.push(p);
    });
    return grouped;
  }, [serverPlans]);

  const baseServerPlans = useMemo(() => {
    return Array.from(groupedServerPlans.values())
      .map((variants) => variants.find((v) => v.variantCode === 'STANDARD') || variants.find((v) => !v.variantCode) || variants[0])
      .sort((a, b) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER));
  }, [groupedServerPlans]);

  const selectedServerVariants = useMemo(() => {
    if (!selectedServerPlanCode) return [];
    return [...(groupedServerPlans.get(selectedServerPlanCode) || [])].sort(
      (a, b) => (a.variantOrder ?? Number.MAX_SAFE_INTEGER) - (b.variantOrder ?? Number.MAX_SAFE_INTEGER)
    );
  }, [groupedServerPlans, selectedServerPlanCode]);

  const selectedServerPlan = useMemo(() => {
    if (!selectedServerPlanCode) return null;
    const byVariant = selectedServerVariants.find(
      (v) => (v.variantCode || 'STANDARD') === (selectedServerVariantCode || 'STANDARD')
    );
    return byVariant || selectedServerVariants[0] || null;
  }, [selectedServerPlanCode, selectedServerVariantCode, selectedServerVariants]);

  useEffect(() => {
    if (!isPricingProject || !config.type || !config.deploymentType || !pricingOsFamily) {
      setServerPlans([]);
      return;
    }

    const load = async () => {
      setIsLoadingServerPlans(true);
      try {
        const plans = await pricingService.getPlans(TYPE_FAMILY, SERVER_OS_TYPE, pricingOsFamily);
        setServerPlans(plans);
        if (plans.length > 0) {
          const grouped = new Map<string, PricingPlan[]>();
          plans.forEach((p) => {
            if (!grouped.has(p.planCode)) grouped.set(p.planCode, []);
            grouped.get(p.planCode)!.push(p);
          });
          const base = Array.from(grouped.values())
            .map((variants) => variants.find((v) => v.variantCode === 'STANDARD') || variants[0])
            .sort((a, b) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER))[0];
          if (base) {
            setSelectedServerPlanCode(base.planCode);
            setSelectedServerVariantCode(base.variantCode || 'STANDARD');
          }
        }
      } catch (error) {
        console.error('Error loading server pricing plans:', error);
        setServerPlans([]);
      } finally {
        setIsLoadingServerPlans(false);
      }
    };

    load();
  }, [isPricingProject, pricingOsFamily, config.type, config.deploymentType]);

  useEffect(() => {
    if (!isPricingProject || !selectedServerPlanCode || !pricingOsFamily) {
      setServerAddons([]);
      setSelectedServerAddonCodes([]);
      return;
    }

    const load = async () => {
      setIsLoadingServerAddons(true);
      try {
        const addons = await pricingService.getPlanAddons(
          TYPE_FAMILY,
          SERVER_OS_TYPE,
          selectedServerPlanCode,
          pricingOsFamily
        );
        setServerAddons(addons);
        setSelectedServerAddonCodes((prev) => prev.filter((code) => addons.some((a) => a.addonCode === code)));
      } catch (error) {
        console.error('Error loading server pricing addons:', error);
        setServerAddons([]);
        setSelectedServerAddonCodes([]);
      } finally {
        setIsLoadingServerAddons(false);
      }
    };

    load();
  }, [isPricingProject, pricingOsFamily, selectedServerPlanCode]);

  const getLegacyAvailablePlans = () => {
    if (!config.type || !config.deploymentType) return null;
    const isStatic = isStaticTechnology(config.type) || isSSGTechnology(config.type, config.framework);
    const appType = isStatic ? 'static' : 'backend';
    const deploymentType = config.deploymentType as ConfigDeploymentType;
    const plansConfig = getPlansByFilters(appType, deploymentType);
    if (plansConfig.length === 0) return null;
    return { type: appType, deploymentType, plans: plansConfig.map((p) => planConfigToComponentFormat(p, t)) };
  };

  const pricingAvailablePlans = useMemo(() => {
    if (!isPricingProject || !config.deploymentType) return null;
    const plans: PlanOption[] = baseServerPlans.map((plan) => ({
      id: plan.planCode,
      name: plan.planName,
      price: plan.priceAmount ?? null,
      features: isStaticProject
        ? [
            `${plan.includedResources?.transferGb ?? '-'} GB ${t('virtualMachines.create.pricing.transferShort')}`,
            `${plan.includedResources?.storageGb ?? '-'} GB ${t('virtualMachines.create.pricing.storageShort')}`,
          ]
        : [
            `${plan.providerConfiguration?.instanceType || '-'} • ${plan.includedResources?.vcpu ?? '-'} vCPU`,
            `${plan.includedResources?.memoryMb ?? '-'} MB RAM`,
            `${plan.includedResources?.storageGb ?? '-'} GB Disk`,
          ],
    }));
    return { type: isStaticProject ? 'static' : 'backend', deploymentType: config.deploymentType as DeploymentType, plans };
  }, [isPricingProject, isStaticProject, config.deploymentType, baseServerPlans, t]);

  const availablePlans = isPricingProject ? pricingAvailablePlans : getLegacyAvailablePlans();

  const handleServerSetSelectedPlan: React.Dispatch<React.SetStateAction<string | null>> = (value) => {
    const next = typeof value === 'function' ? value(selectedServerPlanCode) : value;
    setSelectedServerPlanCode(next);
    if (!next) {
      setSelectedServerVariantCode(null);
      return;
    }
    const variants = groupedServerPlans.get(next) || [];
    const standard = variants.find((v) => v.variantCode === 'STANDARD') || variants[0];
    setSelectedServerVariantCode(standard?.variantCode || 'STANDARD');
  };

  const getRuntimeTechnology = (type: string) => ((type === 'nextjs' || type === 'nuxt') ? 'nodejs' : type);
  const getRuntimeVersions = (technology: string) => {
    if (technology === 'nodejs') return [
      { value: '16', label: 'Node.js 16', recommended: false },
      { value: '18', label: 'Node.js 18', recommended: false },
      { value: '20', label: 'Node.js 20 (LTS)', recommended: false },
      { value: '22', label: 'Node.js 22 (LTS) - Recomendado', recommended: true },
      { value: '24', label: 'Node.js 24 (LTS)', recommended: false },
    ];
    if (technology === 'netcore') return [
      { value: '6', label: '.NET 6', recommended: false },
      { value: '8', label: '.NET 8 (LTS)', recommended: false },
      { value: '9', label: '.NET 9', recommended: false },
      { value: '10', label: '.NET 10 (LTS) - Recomendado', recommended: true },
    ];
    if (technology === 'python') return [
      { value: '3.9', label: 'Python 3.9', recommended: false },
      { value: '3.10', label: 'Python 3.10', recommended: false },
      { value: '3.11', label: 'Python 3.11', recommended: false },
      { value: '3.12', label: 'Python 3.12 - Recomendado', recommended: true },
      { value: '3.13', label: 'Python 3.13', recommended: false },
      { value: '3.14', label: 'Python 3.14', recommended: false },
    ];
    if (technology === 'go') return [
      { value: '1.21.13', label: 'Go 1.21', recommended: false },
      { value: '1.22.12', label: 'Go 1.22', recommended: false },
      { value: '1.23.6', label: 'Go 1.23', recommended: false },
      { value: '1.24.7', label: 'Go 1.24 - Recomendado', recommended: true },
      { value: '1.25.1', label: 'Go 1.25', recommended: false },
    ];
    return [];
  };

  const getPlanType = (planId: string): 'shared' | 'dedicated' | null => {
    if (isPricingProject) return null;
    return getPlanById(planId)?.planType || null;
  };

  const getDeploymentBadge = (type: DeploymentType) => {
    const badges = {
      dev: { emoji: '🟣', text: 'DEV', color: 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300 border-purple-300 dark:border-purple-700' },
      preview: { emoji: '🟡', text: 'PREVIEW', color: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700' },
      prod_lite: { emoji: '🟢', text: 'PROD', color: 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700' },
      prod: { emoji: '🔵', text: 'PROD HA', color: 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-700' },
    };
    return badges[type] || null;
  };

  const getEstimatedCost = () => {
    if (isPricingProject) {
      const base = selectedServerPlan?.priceAmount || 0;
      const addonCost = serverAddons
        .filter((addon) => selectedServerAddonCodes.includes(addon.addonCode))
        .reduce((sum, addon) => sum + (addon.priceAmount || 0), 0);
      let total = base + addonCost;
      if (config.premiumLogs && selectedLogsPlan === 'premium-7days') total += 5;
      if (config.premiumLogs && selectedLogsPlan === 'premium-30days') total += 10;
      return formatCurrency(total, selectedServerPlan?.currency || 'MXN');
    }
    if (!selectedPlan) return '$0.00';
    const plan = availablePlans?.plans.find((p) => p.id === selectedPlan);
    if (!plan || plan.price === null) return t('deployApp.review.customPricing') || 'Custom';
    return `$${plan.price.toFixed(2)}`;
  };

  const isStepValid = () => {
    const isStatic = isStaticTechnology(config.type) || isSSGTechnology(config.type, config.framework);
    const hasSubdomainSlug = (config.subdomain ?? '').trim() && (config.slug ?? '').trim();
    if (step === 1) {
      if (!config.name || !config.type || !config.deploymentType || (!isStatic && !hasSubdomainSlug)) return false;
      if (isPricingProject) return Boolean(selectedServerPlanCode && selectedServerVariantCode);
      return Boolean(selectedPlan);
    }
    if (step === 2 && isServerProject) {
      return Boolean(config.runtime?.version && config.startMode && (config.type === 'netcore' && (config.startMode === 'auto' || config.startMode === 'publish') ? true : config.targetMode));
    }
    return true;
  };

  const handleCreate = async () => {
    const isStatic = isStaticTechnology(config.type) || isSSGTechnology(config.type, config.framework);
    if (!config.name || !config.type || !config.deploymentType) return;

    setIsCreating(true);
    try {
      const appType = isStatic ? 'static' : 'server';
      const selectedProduct = products.find((p) => p.appType === appType);
      if (!selectedProduct?.id) throw new Error('No product found');
      const finalNetworkMode = isStatic ? 'shared' : (networkMode || 'shared');
      const finalComputeMode = isStatic ? 'shared' : (computeMode || 'shared');

      const metadata: Record<string, unknown> = {
        type: 'APP_DEPLOY',
        appName: config.name,
        appType,
        technology: config.framework || config.type,
        networkMode: finalNetworkMode,
        computeMode: finalComputeMode,
        subdomain: (config.subdomain || '').trim() || undefined,
        appConfig: {
          name: config.name,
          description: config.description || '',
          type: config.type,
          environment: config.environment || '',
          autoDeploy: config.autoDeploy !== undefined ? config.autoDeploy : true,
          premiumLogs: config.premiumLogs || false,
          premiumLogsPlan: selectedLogsPlan || null,
          environmentVariables: config.environmentVariables || [],
          deploymentType: config.deploymentType,
          subdomain: (config.subdomain || '').trim() || undefined,
          slug: (config.slug || '').trim() || undefined,
          runtime: config.runtime || undefined,
          startMode: appType === 'server' ? config.startMode : undefined,
          targetMode: appType === 'server' ? config.targetMode : undefined,
        },
      };

      if (isPricingProject && selectedServerPlanCode && selectedServerPlan && pricingOsFamily) {
        metadata.pricingSelection = {
          typeFamily: TYPE_FAMILY,
          osType: SERVER_OS_TYPE,
          osFamily: pricingOsFamily,
          planCode: selectedServerPlanCode,
          variantCode: selectedServerVariantCode || selectedServerPlan.variantCode || 'STANDARD',
          variantName: selectedServerPlan.variantName || selectedServerPlan.displayName || selectedServerPlan.planName,
          addons: selectedServerAddonCodes,
        };
      }

      const createdOrder = await ordersService.create({ productId: selectedProduct.id, quantity: 1, metadata });
      navigate(`/payment-selection/${createdOrder.id}`);
    } catch (error: any) {
      const message = error?.message || t('deployApp.errors.createOrder') || 'Error al crear la orden.';
      alert(message);
      setIsCreating(false);
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6 py-6">
      <button
        onClick={() => navigate('/deploy-apps')}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
      >
        <ArrowLeftOutlined />
        <span className="text-sm font-medium">{t('deployApp.actions.back') || 'Back'}</span>
      </button>

      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{(t('deployApp.title') || 'Deploy App') + ' (Pricing)'}</h1>
      <p className="text-gray-600 dark:text-gray-400 mb-8">{t('deployApp.subtitle') || 'Deploy your application from a Git repository'}</p>

      <ProgressSteps currentStep={step} />

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 mb-6">
        {step === 1 && (
          <div className="space-y-6">
            <Step1DeployApp
              config={config}
              setConfig={setConfig}
              selectedPlan={isPricingProject ? selectedServerPlanCode : selectedPlan}
              setSelectedPlan={isPricingProject ? handleServerSetSelectedPlan : setSelectedPlan}
              networkMode={networkMode}
              setNetworkMode={setNetworkMode}
              computeMode={computeMode}
              setComputeMode={setComputeMode}
              availablePlans={availablePlans}
              getDeploymentBadge={getDeploymentBadge}
              getPlanType={getPlanType}
              staticProjectTypes={staticProjectTypes}
              backendProjectTypes={backendProjectTypes}
              projectTypeDescriptions={projectTypeDescriptions}
              projectTypes={projectTypes}
              isStaticTechnology={isStaticTechnology}
              isSSRTechnology={isSSRTechnology}
              isSSGTechnology={isSSGTechnology}
              onVerifySubdomain={(name, appType) => servicesService.checkSubdomainAvailable(name, appType)}
              hideInfrastructureSection={isServerProject}
              hideAvailablePlansSection={isPricingProject}
            />

            {isPricingProject && (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    {t('virtualMachines.create.pricing.billingPlanLabel')}
                  </h4>
                  {isLoadingServerPlans ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
                  ) : baseServerPlans.length === 0 ? (
                    <p className="text-sm text-red-600 dark:text-red-400">
                      {t('virtualMachines.create.pricing.noPlansForOs')}
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 gap-3">
                      {baseServerPlans.map((plan) => {
                        const isSelected = selectedServerPlanCode === plan.planCode;
                        return (
                          <button
                            key={plan.planCode}
                            onClick={() => handleServerSetSelectedPlan(plan.planCode)}
                            className={`p-4 border-2 rounded-lg text-left transition-all ${
                              isSelected
                                ? 'border-black dark:border-white bg-gray-50 dark:bg-gray-700'
                                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                            }`}
                          >
                            <p className="font-semibold text-gray-900 dark:text-white">{plan.planName}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{plan.planCode}</p>
                            {isStaticProject ? (
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                                {plan.includedResources?.transferGb ?? '-'} GB {t('virtualMachines.create.pricing.transferShort')} •{' '}
                                {plan.includedResources?.storageGb ?? '-'} GB {t('virtualMachines.create.pricing.storageShort')}
                              </p>
                            ) : (
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                                {(plan.providerConfiguration?.instanceType || '-')} • {plan.includedResources?.vcpu ?? '-'} vCPU •{' '}
                                {plan.includedResources?.memoryMb ?? '-'} MB RAM • {plan.includedResources?.storageGb ?? '-'} GB
                              </p>
                            )}
                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                              {t('virtualMachines.create.pricing.basePricePrefix')} {formatCurrency(plan.priceAmount, plan.currency)}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    {t('virtualMachines.create.pricing.variantsLabel')}
                  </h4>
                  {!selectedServerPlanCode ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('virtualMachines.create.pricing.selectPlanForVariants')}
                    </p>
                  ) : selectedServerVariants.length <= 1 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('virtualMachines.create.pricing.noAdditionalVariants')}
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {selectedServerVariants.map((variant) => {
                        const key = variant.variantCode || 'STANDARD';
                        const isSelected = (selectedServerVariantCode || 'STANDARD') === key;
                        return (
                          <button
                            key={`${variant.planCode}-${key}`}
                            onClick={() => setSelectedServerVariantCode(key)}
                            className={`px-3 py-2 rounded-lg border-2 text-left transition-all ${
                              isSelected
                                ? 'border-black dark:border-white bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                                : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                            }`}
                          >
                            <p className="text-sm font-semibold">
                              {variant.variantName || variant.displayName || variant.variantCode || 'STANDARD'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatCurrency(variant.priceAmount, variant.currency)}
                            </p>
                            {isStaticProject ? (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {variant.includedResources?.storageGb ?? '-'} GB {t('virtualMachines.create.pricing.storageShort')} •{' '}
                                {variant.includedResources?.transferGb ?? '-'} GB {t('virtualMachines.create.pricing.transferShort')}
                              </p>
                            ) : (
                              <>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {variant.providerConfiguration?.instanceType || '-'}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {variant.includedResources?.vcpu ?? '-'} vCPU • {variant.includedResources?.memoryMb ?? '-'} MB RAM
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {variant.includedResources?.storageGb ?? '-'} GB {t('virtualMachines.create.pricing.storageShort')} •{' '}
                                  {variant.includedResources?.transferGb ?? '-'} GB {t('virtualMachines.create.pricing.transferShort')}
                                </p>
                              </>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    {t('virtualMachines.create.pricing.addonsTitle')}
                  </h4>
                  {isLoadingServerAddons ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
                  ) : serverAddons.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('virtualMachines.create.pricing.noAddonsAvailable')}</p>
                  ) : (
                    <div className="space-y-2">
                      {serverAddons.map((addon) => {
                        const checked = selectedServerAddonCodes.includes(addon.addonCode);
                        return (
                          <label key={addon.priceId || addon.addonCode} className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) =>
                                setSelectedServerAddonCodes((prev) =>
                                  e.target.checked
                                    ? Array.from(new Set([...prev, addon.addonCode]))
                                    : prev.filter((code) => code !== addon.addonCode)
                                )
                              }
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">{addon.addonName}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{formatCurrency(addon.priceAmount, addon.currency)}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <Step2DeployApp
            config={config}
            setConfig={setConfig}
            selectedLogsPlan={selectedLogsPlan}
            setSelectedLogsPlan={setSelectedLogsPlan}
            getRuntimeTechnology={getRuntimeTechnology}
            getRuntimeVersions={getRuntimeVersions}
            isSSRTechnology={isSSRTechnology}
            isSSGTechnology={isSSGTechnology}
            backendProjectTypes={backendProjectTypes}
          />
        )}

        {step === 3 && (
          <Step3DeployApp
            config={config}
            projectTypes={projectTypes}
            getDeploymentBadge={getDeploymentBadge}
            selectedPlan={isPricingProject ? selectedServerPlanCode : selectedPlan}
            availablePlans={availablePlans}
            getPlanType={getPlanType}
            selectedLogsPlan={selectedLogsPlan}
            isSSRTechnology={isSSRTechnology}
            isSSGTechnology={isSSGTechnology}
            backendProjectTypes={backendProjectTypes}
            getEstimatedCost={getEstimatedCost}
          />
        )}
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={() => step > 1 && setStep(step - 1)}
          disabled={step === 1}
          className={`px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium transition-colors ${
            step === 1 ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          {t('deployApp.actions.back') || 'Back'}
        </button>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/deploy-apps')}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {t('deployApp.actions.cancel') || 'Cancel'}
          </button>
          {step < 3 ? (
            <button
              onClick={() => isStepValid() && setStep(step + 1)}
              disabled={!isStepValid()}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                isStepValid() ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
            >
              {t('deployApp.actions.next') || 'Next'}
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={isCreating}
              className="px-6 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? t('deployApp.actions.creating') || 'Creating order...' : t('deployApp.actions.create') || 'Create Order'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateDeployAppServerPricing;
